import { useId } from 'react';
import { Alert } from '@shared/components';
import { formatIsoDateLong } from '@/app/utils/formatDate';
import { formatMoneyCurrency } from '@/shared/utils/formatMoneyDisplay';
import { FigureList, FigureRow } from '../../FigureList';
import { feeRowLabel, isNegativeAmount } from '../../../utils/cancellationCopy';
import { negativeBalanceWarning } from '../../../utils/negativeBalanceWarning';
import type { CancelAction } from '../../../types/cancellation.types';

interface Props {
    action: Pick<CancelAction, 'estimate' | 'estimateUnavailable' | 'requiresServiceHistory'>;
}

export function CancellationEstimate({ action }: Props) {
    const headingId = useId();
    const { estimate } = action;

    return (
        <section aria-labelledby={headingId} className="space-y-3 rounded-lg border border-mp-border bg-mp-background p-4">
            <h3 id={headingId} className="text-sm font-semibold text-mp-foreground">
                Refund estimate
            </h3>
            {estimate === null || action.estimateUnavailable ? (
                <Alert variant="warning">
                    Estimate unavailable. The balance could not be read just now. Try again in a moment.
                </Alert>
            ) : (
                <>
                    <FigureList>
                        <FigureRow label="Balance held for this agreement" value={formatMoneyCurrency(estimate.fundBalance)} />
                        <FigureRow
                            label={feeRowLabel(estimate, formatIsoDateLong(estimate.coolingOffEndsOn))}
                            value={formatMoneyCurrency(estimate.feeCharged)}
                        />
                        <FigureRow
                            label="Estimated refund"
                            value={estimate.noCustomerRefund ? 'No refund' : formatMoneyCurrency(estimate.refund)}
                            tone="emphasis"
                        />
                    </FigureList>
                    <p className="text-xs text-mp-muted">
                        Estimate as at {formatIsoDateLong(estimate.asAt)}. The final figure is worked out when the
                        cancellation completes.
                    </p>
                    {estimate.noCustomerRefund && (
                        <Alert variant="info" role="note">
                            This campaign does not pay a refund to the customer, so the balance is not paid out.
                        </Alert>
                    )}
                    {isNegativeAmount(estimate.fundBalance) && (
                        <Alert variant="warning" role="note">
                            {action.requiresServiceHistory
                                ? 'The customer has used more servicing than they have paid for. MotorPlan will need to complete this cancellation.'
                                : negativeBalanceWarning(estimate.fundBalance)}
                        </Alert>
                    )}
                </>
            )}
        </section>
    );
}
